import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PublicLeaderboard } from "./Leaderboard";
import { WorldMapDashboard } from "./WorldMapDashboard";
import {
  Rocket, ArrowRight, Shield, Zap, Globe, Users, Cpu, Bot,
  TrendingUp, Coins, Wallet, CreditCard, Store, GraduationCap,
  Heart, Lock, CheckCircle2, Sparkles, BarChart3, Layers, ArrowUpRight,
  Send, MessageCircle, Youtube, Twitter, Play, Quote, Star
} from "lucide-react";
import { useT } from "./i18n";

/* ═══════════════════════════ HOME / LANDING ═══════════════════════════ */
export function HomePage() {
  const { t } = useT();
  // Real-time aggregated stats from backend (no fake counters in pre-registration phase).
  const [stats, setStats] = useState({ usersTotal: 0, merchantsTotal: 0, countriesTotal: 0, transactionsTotal: 0 });
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const r = await import("../api").then((m) => m.default.get("/stats/global"));
        if (mounted) setStats(r.data || {});
      } catch { /* keep previous */ }
    };
    load();
    const id = setInterval(load, 15 * 1000); // refresh every 15s
    return () => { mounted = false; clearInterval(id); };
  }, []);

  return (
    <main data-testid="page-home">
      {/* HERO — «Вход в гиперпространство» */}
      <section className="tg-page tg-hero">
        <div className="tg-hero-grid">
          <div>
            <span className="tg-eyebrow gold"><Sparkles size={12} /> {t("hero.eyebrow")}</span>
            <h1 className="tg-h1">{t("hero.title1")} <span className="accent">{t("hero.title2")}</span> {t("hero.title3")}</h1>
            <p className="tg-lead">
              {t("hero.lead")}
            </p>
            <div style={{ display: "flex", gap: 12, marginTop: 28, flexWrap: "wrap" }}>
              <Link to="/register" className="tg-btn gold" data-testid="hero-cta-airdrop">
                <Sparkles size={16} /> {t("hero.cta_airdrop")}
              </Link>
              <Link to="/how-it-works" className="tg-btn ghost" data-testid="hero-cta-how">
                <Play size={16} /> {t("hero.cta_how")}
              </Link>
            </div>
            {/* Pre-registration banner */}
            <div data-testid="prereg-banner" style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              marginTop: 22, padding: "8px 14px", borderRadius: 999,
              background: "rgba(255,215,0,0.08)", border: "1px solid rgba(255,215,0,0.35)",
              color: "#FFD700", fontWeight: 700, fontSize: 12, letterSpacing: 0.5,
            }}>
              <span style={{ width: 8, height: 8, borderRadius: 4, background: "#FFD700", boxShadow: "0 0 8px #FFD700", animation: "pulse 1.6s infinite" }} />
              ПРЕДРЕГИСТРАЦИОННЫЙ ПЕРИОД ДО СТАРТА ПРОЕКТА
            </div>
            <div data-testid="home-stats" style={{ display: "flex", gap: 32, marginTop: 28, flexWrap: "wrap" }}>
              <div data-testid="stat-users">
                <div className="tg-stat-num blue">{Number(stats.usersTotal || 0).toLocaleString("ru-RU")}</div>
                <div className="tg-stat-label">{t("stat.users")}</div>
              </div>
              <div data-testid="stat-merchants">
                <div className="tg-stat-num gold">{Number(stats.merchantsTotal || 0).toLocaleString("ru-RU")}</div>
                <div className="tg-stat-label">{t("stat.merchants")}</div>
              </div>
              <div data-testid="stat-countries">
                <div className="tg-stat-num">{Number(stats.countriesTotal || 0).toLocaleString("ru-RU")}</div>
                <div className="tg-stat-label">{t("stat.countries")}</div>
              </div>
              <div data-testid="stat-tx">
                <div className="tg-stat-num" style={{ color: "#10F4A7" }}>{Number(stats.transactionsTotal || 0).toLocaleString("ru-RU")}</div>
                <div className="tg-stat-label">Транзакций</div>
              </div>
            </div>
            <div style={{ fontSize: 11, color: "var(--tg-text-hint)", marginTop: 10 }} data-testid="stats-live-label">
              ⚡ Обновляется в реальном времени · каждые 15 секунд
            </div>
          </div>
          <div className="tg-hero-visual">
            <div className="tg-hero-ring" />
            <div className="tg-hero-ring inner" />
            <div className="tg-hero-orb" />
            <div className="tg-hero-chip" style={{ top: "8%",  left: "2%"  }}>⚡ Turbo Pay</div>
            <div className="tg-hero-chip" style={{ top: "30%", right: "0%" }}>💠 Staking 20%</div>
            <div className="tg-hero-chip" style={{ bottom: "22%", left: "0%" }}>🤖 AI-Agent</div>
            <div className="tg-hero-chip" style={{ bottom: "6%", right: "4%", color: "var(--tg-gold)", borderColor: "rgba(255,215,0,0.35)" }}>🐻 Turbomishka</div>
          </div>
        </div>
      </section>

      {/* TICKER */}
      <section className="tg-ticker">
        <div className="tg-ticker-track">
          {[...Array(2)].flatMap((_, k) => [
            ["TGC/USDT", "$0.10", "+2.5%", true],
            ["tBTC/USDT", "$68,420", "-1.2%", false],
            ["tETH/USDT", "$3,520", "+0.8%", true],
            ["24h VOL", "$12.4M", "+18%", true],
            ["Staking TVL", "$4.2M", "+9%", true],
            ["Active Wallets", "14,328", "+312 today", true],
            ["TGC/USD", "$0.10", "+2.5%", true],
            ["Merchants", "640+", "+12 today", true],
          ].map((row, i) => (
            <div key={`${k}-${i}`} className="tg-ticker-item">
              <span className="dot" /> <b>{row[0]}</b> <span>{row[1]}</span>
              <span className={row[3] ? "up" : "down"}>{row[2]}</span>
            </div>
          )))}
        </div>
      </section>

      {/* КАК НАЧАТЬ — 4 шага */}
      <section className="tg-page tg-section">
        <span className="tg-eyebrow">{t("how.eyebrow")}</span>
        <h2 className="tg-h2" style={{ marginTop: 16 }}>{t("how.title")}</h2>
        <p className="tg-lead" style={{ marginBottom: 40 }}>{t("how.lead")}</p>
        <div className="tg-bento">
          {[
            { n: "01", t: t("how.step1.t"), d: t("how.step1.d"), ic: "📱" },
            { n: "02", t: t("how.step2.t"), d: t("how.step2.d"), ic: "🎁" },
            { n: "03", t: t("how.step3.t"), d: t("how.step3.d"), ic: "⚡" },
            { n: "04", t: t("how.step4.t"), d: t("how.step4.d"), ic: "🚀" },
          ].map((s, i) => (
            <div key={i} className="tg-card wide" style={{ position: "relative", overflow: "hidden" }}>
              <div style={{ position: "absolute", top: -20, right: -10, fontSize: 90, fontWeight: 900, color: "rgba(0,242,255,0.08)", letterSpacing: -4 }}>{s.n}</div>
              <div style={{ fontSize: 36, marginBottom: 10, position: "relative" }}>{s.ic}</div>
              <h3 className="tg-h3" style={{ position: "relative" }}>{s.t}</h3>
              <p style={{ color: "var(--tg-text-dim)", fontSize: 14, position: "relative" }}>{s.d}</p>
            </div>
          ))}
        </div>
        <div style={{ textAlign: "center", marginTop: 40 }}>
          <Link to="/register" className="tg-btn primary" data-testid="start-now-btn">
            <Rocket size={16} /> {t("how.cta")}
          </Link>
        </div>
      </section>

      {/* 6 ECOSYSTEM PILLARS */}
      <section className="tg-page tg-section">
        <span className="tg-eyebrow">{t("eco.eyebrow")}</span>
        <h2 className="tg-h2" style={{ marginTop: 16 }}>{t("eco.title")}</h2>
        <p className="tg-lead" style={{ marginBottom: 40 }}>{t("eco.lead")}</p>
        <div className="tg-bento">
          {[
            { ic: <Bot size={24} />,    t: t("eco.ai.t"),    d: t("eco.ai.d"),    to: "/ai-agent",      col: "#00F2FF" },
            { ic: <Wallet size={24} />, t: t("eco.money.t"), d: t("eco.money.d"), to: "/app-info",      col: "#10F4A7" },
            { ic: <Store size={24} />,  t: t("eco.shop.t"),  d: t("eco.shop.d"),  to: "/marketplace",   col: "#FFD700" },
            { ic: <TrendingUp size={24} />, t: t("eco.stake.t"), d: t("eco.stake.d"), to: "/staking-info", col: "#BC13FE" },
            { ic: <GraduationCap size={24} />, t: t("eco.acad.t"), d: t("eco.acad.d"), to: "/academy", col: "#FF3D6C" },
            { ic: <Heart size={24} />,  t: t("eco.char.t"), d: t("eco.char.d"), to: "/charity", col: "#F97316" },
          ].map((c, i) => (
            <Link key={i} to={c.to} className="tg-card wide"
              style={{ textDecoration: "none", color: "inherit", display: "block" }}>
              <div className="tg-dropdown-icon" style={{ marginBottom: 14, color: c.col, borderColor: c.col + "55", background: c.col + "18" }}>{c.ic}</div>
              <h3 className="tg-h3">{c.t}</h3>
              <p style={{ color: "var(--tg-text-dim)", fontSize: 14 }}>{c.d}</p>
              <div style={{ color: c.col, fontSize: 13, fontWeight: 700, marginTop: 10, display: "inline-flex", alignItems: "center", gap: 4 }}>
                {t("eco.more")} <ArrowRight size={14} />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* AIRDROP big banner */}
      <section className="tg-page tg-section-tight">
        <div className="tg-card" style={{
          background: "linear-gradient(135deg, rgba(255,215,0,0.12), rgba(188,19,254,0.1))",
          padding: 48, textAlign: "center", border: "1px solid rgba(255,215,0,0.3)"
        }}>
          <div className="tg-eyebrow gold">{t("airdrop.eyebrow")}</div>
          <h2 className="tg-h2" style={{ marginTop: 14 }}>{t("airdrop.title1")} <span style={{ color: "#FFD700" }}>{t("airdrop.bonus")}</span></h2>
          <p className="tg-lead" style={{ margin: "0 auto 24px" }}>{t("airdrop.lead")}</p>
          <Link to="/register" className="tg-btn gold">{t("airdrop.cta")}</Link>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="tg-page tg-section">
        <span className="tg-eyebrow">{t("test.eyebrow")}</span>
        <h2 className="tg-h2" style={{ marginTop: 16 }}>{t("test.title")}</h2>
        <div className="tg-bento">
          {[
            { n: t("pages.e86db5"), r: t("pages.77a517"), text: t("pages.2fe29a"), avatar: "🇰🇿" },
            { n: t("pages.a4b3a4"), r: t("pages.a48bbe"), text: t("pages.0063b7"), avatar: "🇺🇿" },
            { n: "Marcus T.", r: "Dubai", text: "Invited 44 friends, got 5000 TGC bonus + Diamond stake. The referral tree visualization is on another level.", avatar: "🇦🇪" },
            { n: t("pages.637509"), r: t("pages.e54579"), text: t("pages.1fa01c"), avatar: "🇰🇿" },
            { n: "Aisha H.", r: "Manila", text: t("pages.2903b0"), avatar: "🇵🇭" },
            { n: "Dmitry R.", r: t("pages.e6eb3a"), text: t("pages.7a5511"), avatar: "🇷🇺" },
          ].map((t, i) => (
            <div key={i} className="tg-card wide">
              <Quote size={20} color="#00F2FF" opacity={0.5} />
              <p style={{ fontSize: 14, lineHeight: 1.6, marginTop: 10 }}>{t.text}</p>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 16, paddingTop: 14, borderTop: "1px dashed var(--tg-border)" }}>
                <div style={{ fontSize: 24 }}>{t.avatar}</div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>{t.n}</div>
                  <div className="tg-stat-label" style={{ margin: 0 }}>{t.r}</div>
                </div>
                <div style={{ marginLeft: "auto", color: "#FFD700", display: "flex" }}>
                  {[...Array(5)].map((_, j) => <Star key={j} size={12} fill="#FFD700" />)}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* COMMUNITY */}
      <section className="tg-page tg-section">
        <span className="tg-eyebrow">{t("comm.eyebrow")}</span>
        <h2 className="tg-h2" style={{ marginTop: 16 }}>{t("comm.title")}</h2>
        <div className="tg-bento">
          {[
            { ic: <Send size={28} />, t: "Telegram News", sub: "+12,400", d: t("pages.95d35e"), href: "https://t.me/turboglobalcoin", col: "#00B4FF" },
            { ic: <MessageCircle size={28} />, t: "Telegram Chat", sub: "+8,900", d: t("pages.8e106a"), href: "https://t.me/turboglobalchat", col: "#10F4A7" },
            { ic: <Youtube size={28} />, t: "YouTube", sub: "+3,200", d: t("pages.633c6a"), href: "https://youtube.com/@turboglobalcoin", col: "#FF3D6C" },
            { ic: <Twitter size={28} />, t: "X / Twitter", sub: "+5,600", d: t("pages.500bde"), href: "https://twitter.com/turboglobalcoin", col: "#FFD700" },
          ].map((c, i) => (
            <a key={i} href={c.href} target="_blank" rel="noreferrer" className="tg-card"
              style={{ textDecoration: "none", color: "inherit", display: "block" }}>
              <div className="tg-dropdown-icon" style={{ marginBottom: 14, color: c.col, borderColor: c.col + "55", background: c.col + "18" }}>{c.ic}</div>
              <h3 className="tg-h3">{c.t}</h3>
              <div className="tg-stat-num" style={{ color: c.col, fontSize: 20, margin: "6px 0" }}>{c.sub}</div>
              <p style={{ color: "var(--tg-text-dim)", fontSize: 13 }}>{c.d}</p>
            </a>
          ))}
        </div>
      </section>

      {/* MINI FAQ FOR USERS */}
      <section className="tg-page tg-section">
        <span className="tg-eyebrow">{t("faq.eyebrow")}</span>
        <h2 className="tg-h2" style={{ marginTop: 16 }}>{t("faq.title")}</h2>
        <div className="tg-bento">
          {[
            [t("faq.q1"), t("faq.a1")],
            [t("faq.q2"), t("faq.a2")],
            [t("faq.q3"), t("faq.a3")],
            [t("faq.q4"), t("faq.a4")],
            [t("faq.q5"), t("faq.a5")],
            [t("faq.q6"), t("faq.a6")],
          ].map(([q, a], i) => (
            <div key={i} className="tg-card wide">
              <h3 className="tg-h3" style={{ fontSize: 16 }}>{q}</h3>
              <p style={{ color: "var(--tg-text-dim)", fontSize: 14, marginTop: 8 }}>{a}</p>
            </div>
          ))}
        </div>
        <div style={{ textAlign: "center", marginTop: 24 }}>
          <Link to="/faq" className="tg-btn ghost">{t("faq.all")} <ArrowRight size={14} /></Link>
        </div>
      </section>

      {/* PUBLIC LEADERBOARD — pre-registration ecosystem ranking */}
      <PublicLeaderboard />

      {/* 2D WORLD-MAP DASHBOARD — live global activity */}
      <WorldMapDashboard />

      {/* FINAL CTA */}
      <section className="tg-page" style={{ padding: "60px 0" }}>
        <div className="tg-card" style={{ textAlign: "center", padding: 48, background: "radial-gradient(900px 400px at 50% 0%, rgba(0,242,255,0.12), transparent 60%)" }}>
          <h2 className="tg-h2">{t("final.title")}</h2>
          <p className="tg-lead" style={{ margin: "0 auto 28px" }}>{t("final.lead")}</p>
          <div style={{ display: "inline-flex", gap: 12, flexWrap: "wrap", justifyContent: "center" }}>
            <Link to="/register" className="tg-btn gold"><Sparkles size={16} /> {t("final.cta_airdrop")}</Link>
            <Link to="/how-it-works" className="tg-btn ghost">{t("final.cta_how")} <ArrowRight size={16} /></Link>
          </div>
        </div>
      </section>
    </main>
  );
}

/* ═══════════════════ Universal page helper ═══════════════════ */
export function PageHero({ eyebrow, title, lead, accent }) {
  return (
    <section className="tg-page" style={{ padding: "80px 0 40px" }}>
      <span className={`tg-eyebrow ${accent === "gold" ? "gold" : ""}`}>{eyebrow}</span>
      <h1 className="tg-h1" style={{ maxWidth: 900 }}>{title}</h1>
      {lead && <p className="tg-lead">{lead}</p>}
    </section>
  );
}

/* ═══════════════════════════ TECHNOLOGY ═══════════════════════════ */
export function TechnologyPage() {
  const { t } = useT();
  return (
    <main data-testid="page-technology">
      <PageHero eyebrow={t("pages.633239")} title={<>{t("pages.b0d4a7")}<span className="accent">{t("pages.2ede45")}</span></>}
        lead={t("pages.444787")} />
      <section className="tg-page tg-section-tight">
        <div className="tg-bento">
          {[
            { t: "Matching Engine", d: "Rust + Tokio. 100k ops/sec, 99.99% uptime SLA. Latency p99 < 2ms.", i: "⚡" },
            { t: "Smart Contracts", d: "ERC-20 TGC + 0.5% burn + staking rewards + referral cascade. Audited by CertiK/Hacken Q3 2026.", i: "📜" },
            { t: "API Gateway", d: "REST + WebSocket. Rate-limited, JWT-authed, OpenAPI 3.1 spec. 50+ endpoints.", i: "🔌" },
            { t: "Cold Wallets", d: t("pages.458af0"), i: "🧊" },
            { t: "KYC/AML", d: t("pages.bbede0"), i: "🪪" },
            { t: "Card Acquiring", d: "Wallester Visa (Estonia) + Unlimit fallback. 100M+ POS, Apple/Google Pay.", i: "💳" },
          ].map((c, i) => (
            <div key={i} className="tg-card wide">
              <div style={{ fontSize: 32, marginBottom: 10 }}>{c.i}</div>
              <h3 className="tg-h3">{c.t}</h3>
              <p style={{ color: "var(--tg-text-dim)", fontSize: 14 }}>{c.d}</p>
            </div>
          ))}
        </div>
      </section>
      <section className="tg-page tg-section">
        <h2 className="tg-h2">{t("pages.6f35af")}</h2>
        <div className="tg-card">
          <table className="tg-table">
            <thead><tr><th>{t("pages.acb334")}</th><th>{t("pages.49dec8")}</th><th>{t("pages.1cdafd")}</th><th>{t("pages.7203f7")}</th></tr></thead>
            <tbody>
              {[
                ["L1 UI", "Mobile App (PWA)", "React 19 + Zustand + Motion", "✅ Production"],
                ["L1 UI", "Corporate Website", "React Router + Glass UI", "✅ Production"],
                ["L2 Proto", "Matching Engine", "Rust + Tokio + Redis", "🏗️ Q2 2026"],
                ["L2 Proto", "TGC Token ERC-20", "Solidity 0.8 / Foundry", "🏗️ Q2 2026"],
                ["L2 Proto", "Oracle price feed", "Chainlink + Binance API", "📅 Q3 2026"],
                ["L3 Sec", "Air-gapped HSM", "Ledger Enterprise + YubiKey", "📅 Q3 2026"],
                ["L3 Sec", "Audit", "CertiK + Hacken", "📅 Q3 2026"],
                ["Integr.", "Wallester Visa", "Tilisi API", "📅 Q4 2026"],
                ["Integr.", "Sumsub KYC", "Sumsub WebSDK", "📅 Q3 2026"],
              ].map((row, i) => (
                <tr key={i}>{row.map((c, j) => <td key={j}>{c}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </main>
  );
}

/* ═══════════════════════════ AI AGENT ═══════════════════════════ */
export function AIAgentPage() {
  const { t } = useT();
  return (
    <main data-testid="page-ai">
      <PageHero eyebrow="Turbo AI-Agent" title={<>{t("pages.ef28b9")}<span className="accent">{t("pages.99e2b5")}</span> 24/7</>}
        lead={t("pages.3ea096")} />
      <section className="tg-page tg-section-tight">
        <div className="tg-bento">
          {[
            [t("pages.ce0115"), t("pages.d0370d")],
            [t("pages.7f6619"), t("pages.b9131c")],
            [t("pages.cafa22"), t("pages.bd298a")],
            ["💬 P2P matcher", t("pages.832336")],
            [t("pages.1715c9"), t("pages.aef93b")],
            ["🛡️ Anti-scam", t("pages.f0199d")],
          ].map(([t, d], i) => (
            <div key={i} className="tg-card wide">
              <h3 className="tg-h3">{t}</h3>
              <p style={{ color: "var(--tg-text-dim)", fontSize: 14 }}>{d}</p>
            </div>
          ))}
        </div>
      </section>
      <section className="tg-page tg-section">
        <div className="tg-card tg-card-glow" style={{ padding: 36 }}>
          <span className="tg-eyebrow">Demo</span>
          <h2 className="tg-h2" style={{ marginTop: 14 }}>{t("pages.1a92d7")}</h2>
          <p className="tg-lead">{t("pages.0a812f")}</p>
          <Link to="/app" className="tg-btn primary" style={{ marginTop: 16 }}><Bot size={16} />{t("pages.e54caa")}</Link>
        </div>
      </section>
    </main>
  );
}

/* ═══════════════════════════ SOCIAL 40/40/10/10 ═══════════════════════════ */
export function SocialPage() {
  const { t } = useT();
  const rows = [
    ["40%", t("pages.fb57fd"), "Staking rewards + Energy Battery multiplier", "#00F2FF"],
    ["40%", t("pages.c1da7d"), t("pages.730a53"), "#FFD700"],
    ["10%", t("pages.56eb0c"), t("pages.3c257b"), "#10F4A7"],
    ["10%", "Resilience Fund + Charity", t("pages.4903a9"), "#FF3D6C"],
  ];
  return (
    <main data-testid="page-social">
      <PageHero eyebrow={t("pages.740777")} title={<>{t("pages.1a534a")}<span className="accent">40 / 40 / 10 / 10</span></>}
        lead={t("pages.1f8424")} />
      <section className="tg-page tg-section-tight">
        <div style={{ display: "grid", gap: 14 }}>
          {rows.map(([pct, title, desc, col], i) => (
            <div key={i} className="tg-card" style={{ display: "flex", alignItems: "center", gap: 24, flexWrap: "wrap", borderLeft: `4px solid ${col}` }}>
              <div className="tg-stat-num" style={{ color: col, minWidth: 120 }}>{pct}</div>
              <div style={{ flex: 1 }}>
                <h3 className="tg-h3">{title}</h3>
                <p style={{ color: "var(--tg-text-dim)", fontSize: 14, margin: 0 }}>{desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
}

/* ═══════════════════════════ APP INFO ═══════════════════════════ */
export function AppInfoPage() {
  const { t } = useT();
  return (
    <main data-testid="page-app">
      <PageHero eyebrow={t("header.products.app.title")} title={<>TGC App — <span className="accent">{t("pages.36ad5c")}</span></>}
        lead={t("pages.e04acf")} />
      <section className="tg-page tg-section-tight">
        <div className="tg-bento">
          {[
            ["🚀", t("pages.33debb"), t("pages.7f2341")],
            ["💰", t("pages.0e7831"), t("pages.a8f338")],
            ["📈", t("pages.02eb22"), "€100/8% · €500/10% · €1000/13% · €5000/17% · €10000/20%"],
            ["👥", t("pages.f2a453"), t("pages.1db466")],
            ["📊", t("pages.ea8de5"), "TGC/USDT, tBTC, tETH · market + limit · full OHLCV"],
            ["🤝", "P2P exchange", t("pages.0739f2")],
            ["🛍️", "Marketplace", t("pages.2444e7")],
            ["💳", "Virtual Visa", t("pages.04eb4f")],
            ["🤖", "AI Agent", t("pages.2f3949")],
          ].map(([ic, t, d], i) => (
            <div key={i} className="tg-card wide">
              <div style={{ fontSize: 28, marginBottom: 10 }}>{ic}</div>
              <h3 className="tg-h3">{t}</h3>
              <p style={{ color: "var(--tg-text-dim)", fontSize: 14 }}>{d}</p>
            </div>
          ))}
        </div>
        <div style={{ textAlign: "center", marginTop: 48 }}>
          <Link to="/app" className="tg-btn primary"><Rocket size={16} />{t("pages.2775a2")}</Link>
        </div>
      </section>
    </main>
  );
}

/* ═══════════════════════════ BUSINESS (MERCHANT) ═══════════════════════════ */
export function BusinessPage() {
  const { t } = useT();
  return (
    <main data-testid="page-business">
      <PageHero eyebrow="Turbo Business" title={<>Merchant API — <span className="accent">{t("pages.6efa2c")}</span></>}
        lead={t("pages.85f438")} />
      <section className="tg-page tg-section-tight">
        <div className="tg-bento">
          {[
            [t("pages.6b43f5"), t("pages.e2828a")],
            [t("pages.ab9f45"), t("pages.4fedfc")],
            [t("pages.0951db"), t("pages.78c515")],
            [t("pages.e88cdd"), t("pages.f72275")],
            ["📊 Dashboard", t("pages.7459f7")],
            ["🎁 Energy Boost", t("pages.1a8b61")],
          ].map(([t, d], i) => (
            <div key={i} className="tg-card wide">
              <h3 className="tg-h3">{t}</h3>
              <p style={{ color: "var(--tg-text-dim)", fontSize: 14 }}>{d}</p>
            </div>
          ))}
        </div>
      </section>
      <section className="tg-page tg-section">
        <div className="tg-card">
          <h2 className="tg-h2">{t("pages.a49106")}</h2>
          <table className="tg-table">
            <thead><tr><th>{t("pages.c1019e")}</th><th>{t("pages.400a03")}</th><th>{t("pages.fd6c2c")}</th><th>{t("pages.404752")}</th><th>SLA</th></tr></thead>
            <tbody>
              <tr><td><b>Starter</b></td><td>0.6%</td><td>{t("pages.84e384")}</td><td>{t("pages.09825a")}</td><td>99.5%</td></tr>
              <tr><td><b>Business</b></td><td>0.1-0.5%</td><td>{t("pages.a1df7d")}</td><td>{t("pages.328638")}</td><td>99.9%</td></tr>
              <tr><td><b>Enterprise</b></td><td>{t("pages.963eac")}</td><td>∞</td><td>+ White-label</td><td>99.99%</td></tr>
            </tbody>
          </table>
        </div>
      </section>
    </main>
  );
}

/* ═══════════════════════════ MARKETPLACE ═══════════════════════════ */
export function MarketplacePage() {
  const { t } = useT();
  const items = [
    ["AirPods Pro 3", "€250", "🎧"], ["iPhone 17 Pro Max", "€1,200", "📱"],
    ["Nike Air Max 2026", "€180", "👟"], ["Turbo Hoodie", "€65", "🧥"],
    ["Xiaomi 15 Watch", "€199", "⌚"], ["Tesla Model Y deposit", "€2,000", "🚗"],
    ["Turbo Coffee 1kg", "€22", "☕"], ["Energy Drinks Case", "€15", "⚡"],
  ];
  return (
    <main data-testid="page-marketplace">
      <PageHero eyebrow="Turbo Marketplace" title={<>{t("pages.0eb751")}<span className="accent">Shopping Wallet</span></>}
        lead={t("pages.e6f965")} />
      <section className="tg-page tg-section-tight">
        <div className="tg-bento" style={{ gridTemplateColumns: "repeat(4, 1fr)" }}>
          {items.map(([n, p, i], k) => (
            <div key={k} className="tg-card" style={{ gridColumn: "span 1", textAlign: "center" }}>
              <div style={{ fontSize: 48, marginBottom: 8 }}>{i}</div>
              <div style={{ fontSize: 14, fontWeight: 700 }}>{n}</div>
              <div className="tg-stat-label" style={{ margin: "6px 0" }}>{p}</div>
              <div className="tg-eyebrow gold" style={{ fontSize: 10 }}>{(parseFloat(p.replace(/[^\d.]/g,""))/0.10).toFixed(0)} TGC</div>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
}

/* ═══════════════════════════ ACADEMY ═══════════════════════════ */
export function AcademyPage() {
  const { t } = useT();
  return (
    <main data-testid="page-academy">
      <PageHero eyebrow="Turbo Academy" title={<>{t("pages.1a0eb8")}<span className="accent">Web3 + DeFi</span>{t("pages.01dc3e")}</>}
        lead={t("pages.cd0f68")} />
      <section className="tg-page tg-section-tight">
        <div className="tg-bento">
          {[
            [t("pages.0731ca"), "Crypto 101 · Trading · Staking · Security · Tokenomics", t("pages.6d96fd")],
            [t("pages.216559"), t("pages.85e9d8"), t("pages.548e4b")],
            [t("pages.70cd2b"), t("pages.c26d68"), t("pages.e3122f")],
            [t("pages.472dbb"), t("pages.9875c9"), t("pages.5cdcf9")],
            [t("pages.0b6d44"), t("pages.4066d8"), t("pages.237486")],
            ["🎁 TGC Rewards", t("pages.d0e30b"), t("pages.a19a72")],
          ].map(([t, d, ex], i) => (
            <div key={i} className="tg-card wide">
              <h3 className="tg-h3">{t}</h3>
              <p style={{ color: "var(--tg-text-dim)", fontSize: 14, marginBottom: 10 }}>{d}</p>
              <span className="tg-eyebrow gold">{ex}</span>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
}

/* ═══════════════════════════ CHARITY ═══════════════════════════ */
export function CharityPage() {
  const { t } = useT();
  const [stats, setStats] = React.useState(null);
  React.useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const backend = process.env.REACT_APP_BACKEND_URL || "";
        const r = await fetch(`${backend}/api/tgc/public/stats`);
        if (r.ok && alive) setStats(await r.json());
      } catch (_) { /* silent */ }
    };
    load();
    const iv = setInterval(load, 15000);
    return () => { alive = false; clearInterval(iv); };
  }, []);
  const totalTgc = Number(stats?.charityTotalTgc || 0);
  const txCount = Number(stats?.charityTxCount || 0);
  const totalEur = Number(stats?.charityTotalEur || 0);
  return (
    <main data-testid="page-charity">
      <PageHero eyebrow="Bear Charity Mission" accent="gold" title={<>{t("pages.f07cd9")}<span className="accent">{t("pages.a67bdd")}</span></>}
        lead={t("pages.4b0d22")} />

      {/* LIVE COUNTERS — real data from /api/tgc/public/stats */}
      <section className="tg-page tg-section-tight">
        <div className="tg-bento">
          {[
            { n: txCount.toLocaleString("ru-RU"), l: "Пожертвований (шт.)", col: "#FF3D6C", testId: "charity-tx-count" },
            { n: totalTgc.toLocaleString("ru-RU", { maximumFractionDigits: 2 }) + " TGC", l: "Собрано в фонд", col: "#10F4A7", testId: "charity-total-tgc" },
            { n: "€" + totalEur.toLocaleString("ru-RU", { maximumFractionDigits: 0 }), l: "Эквивалент в EUR", col: "#FFD700", testId: "charity-total-eur" },
          ].map((c, i) => (
            <div key={i} className="tg-card wide" style={{ textAlign: "center", position: "relative" }}>
              <div style={{ position: "absolute", top: 14, right: 14, display: "flex", alignItems: "center", gap: 4 }}>
                <div style={{ width: 8, height: 8, borderRadius: 4, background: "#10F4A7", animation: "pulse 1.2s infinite" }} />
                <span className="tg-stat-label" style={{ margin: 0 }}>LIVE</span>
              </div>
              <div className="tg-stat-num" data-testid={c.testId} style={{ color: c.col, fontFeatureSettings: '"tnum"' }}>{c.n}</div>
              <div className="tg-stat-label">{c.l}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="tg-page tg-section-tight">
        <div className="tg-bento">
          {[
            ["🐻", t("pages.432a93"), t("pages.a66964")],
            ["🌳", t("pages.e3e55b"), t("pages.b36e66")],
            ["📚", t("pages.4b8547"), t("pages.919216")],
            ["💊", t("pages.727a81"), t("pages.498ea2")],
            ["🌊", t("pages.48efab"), t("pages.abdb95")],
            ["👵", t("pages.0d1c51"), t("pages.16f536")],
          ].map(([ic, t, d], i) => (
            <div key={i} className="tg-card wide">
              <div style={{ fontSize: 40, marginBottom: 10 }}>{ic}</div>
              <h3 className="tg-h3">{t}</h3>
              <p style={{ color: "var(--tg-text-dim)", fontSize: 14 }}>{d}</p>
            </div>
          ))}
        </div>
        <div className="tg-card tg-card-glow" style={{ marginTop: 40, textAlign: "center", padding: 36 }}>
          <h3 className="tg-h3">{t("pages.47d8fa")}</h3>
          <p style={{ color: "var(--tg-text-dim)" }}>{t("pages.edbe37")}</p>
        </div>
      </section>
    </main>
  );
}

/* ═══════════════════════════ HOW IT WORKS ═══════════════════════════ */
export function HowItWorksPage() {
  const { t } = useT();
  return (
    <main data-testid="page-how-it-works">
      <PageHero eyebrow={t("pages.164ea2")} title={<>{t("pages.8a2e11")}<span className="accent">{t("pages.9e8649")}</span>{t("pages.81fddc")}</>}
        lead={t("pages.60cc2e")} />
      <section className="tg-page tg-section-tight">
        <div className="tg-card" style={{ padding: 40 }}>
          {[
            { ph: t("pages.02edb5"), d: t("pages.781d15") },
            { ph: t("pages.457e2d"), d: t("pages.ec9e0b") },
            { ph: t("pages.cb7ce8"), d: t("pages.dd4ab6") },
            { ph: t("pages.ae4d7a"), d: t("pages.cdf145") },
            { ph: t("pages.9cfb83"), d: t("pages.d0246b") },
            { ph: t("pages.c12bee"), d: t("pages.ebe41d") },
          ].map((p, i) => (
            <div key={i} className={`tg-road-item ${i < 3 ? "done" : ""}`}>
              <span className="tg-road-dot" />
              <div className="tg-road-phase">{p.ph}</div>
              <p style={{ color: "var(--tg-text-dim)", fontSize: 15, marginTop: 8 }}>{p.d}</p>
            </div>
          ))}
        </div>
        <div style={{ textAlign: "center", marginTop: 32 }}>
          <Link to="/app" className="tg-btn gold" data-testid="how-start-btn">
            <Sparkles size={16} />{t("pages.7368ba")}</Link>
        </div>
      </section>
    </main>
  );
}

/* ═══════════════════════════ STAKING INFO ═══════════════════════════ */
export function StakingInfoPage() {
  const { t } = useT();
  const pkgs = [
    { n: "Starter",  eur: 100,   rate: 8,  days: 30,  col: "#00F2FF" },
    { n: "Bronze",   eur: 500,   rate: 10, days: 60,  col: "#10F4A7" },
    { n: "Silver",   eur: 1000,  rate: 13, days: 90,  col: "#FFD700" },
    { n: "Gold",     eur: 5000,  rate: 17, days: 150, col: "#F97316" },
    { n: "Diamond",  eur: 10000, rate: 20, days: 300, col: "#BC13FE" },
  ];
  return (
    <main data-testid="page-staking">
      <PageHero eyebrow="Turbo Staking" title={<>{t("pages.c1c57f")}<span className="accent">{t("pages.e573e8")}</span></>}
        lead={t("pages.7f0462")} />
      <section className="tg-page tg-section-tight">
        <div className="tg-bento">
          {pkgs.map((p) => {
            const months = Math.round(p.days / 30);
            const profit = Math.round(p.eur * (p.rate / 100) * months);
            return (
              <div key={p.n} className="tg-card wide" style={{ borderColor: p.col + "55", position: "relative", overflow: "hidden" }}>
                <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 4, background: p.col }} />
                <div className="tg-eyebrow" style={{ background: p.col + "22", borderColor: p.col + "55", color: p.col }}>
                  {p.n.toUpperCase()}
                </div>
                <div className="tg-stat-num" style={{ color: p.col, marginTop: 14 }}>{p.rate}%</div>
                <div className="tg-stat-label">{t("pages.b979f2")}</div>
                <div style={{ marginTop: 18, paddingTop: 18, borderTop: "1px dashed var(--tg-border)" }}>
                  <div className="row between" style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                    <span style={{ color: "var(--tg-text-dim)" }}>{t("pages.e48e09")}</span>
                    <b>€{p.eur.toLocaleString("ru-RU")}</b>
                  </div>
                  <div className="row between" style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginTop: 6 }}>
                    <span style={{ color: "var(--tg-text-dim)" }}>{t("pages.4cf269")}</span>
                    <b>{p.days} {t("pages.c18365")}</b>
                  </div>
                  <div className="row between" style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginTop: 6 }}>
                    <span style={{ color: "var(--tg-text-dim)" }}>{t("pages.243c85")}</span>
                    <b style={{ color: "#10F4A7" }}>+€{profit.toLocaleString("ru-RU")}</b>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>
      <section className="tg-page tg-section">
        <div className="tg-card tg-card-glow" style={{ padding: 36 }}>
          <h3 className="tg-h3">{t("pages.6f320a")}</h3>
          <p style={{ color: "var(--tg-text-dim)", fontSize: 15, lineHeight: 1.7, marginTop: 10 }}>{t("pages.e9e6dd")}</p>
          <div style={{ marginTop: 20 }}>
            <Link to="/app" className="tg-btn primary"><TrendingUp size={16} />{t("pages.207573")}</Link>
          </div>
        </div>
      </section>
    </main>
  );
}
